import { TLogErrorOptions } from '@models/common';
import { logError } from '@utils/errors';

/**
 * Owns a fire-and-forget promise. Without this the rejection reaches the global
 * `unhandledrejection` handler, where it arrives with `handled: false` and — in
 * WebKit — often without a parseable stack, so it cannot be attributed to the
 * code that started it.
 */
export const voidPromise = (
  promise: Promise<unknown>,
  options?: TLogErrorOptions
): void => {
  promise.catch((error) => logError(error, options));
};

/**
 * Runs a callback that may be sync or async and reports either failure mode
 * (a synchronous throw or a rejected promise) instead of letting it escape.
 */
export const runSafely = (
  fn: () => unknown,
  options?: TLogErrorOptions
): void => {
  try {
    const result = fn();

    if (result instanceof Promise) {
      voidPromise(result, options);
    }
  } catch (error) {
    logError(error, options);
  }
};

/**
 * Wraps an async function so it can be passed where a sync callback is expected
 * (`onClick`, `onSubmit`, effects) without leaking a floating promise.
 */
export const safeAsync =
  <TArgs extends unknown[]>(
    fn: (...args: TArgs) => unknown,
    options?: TLogErrorOptions
  ) =>
  (...args: TArgs): void => {
    runSafely(() => fn(...args), options);
  };

/**
 * Resolves after `ms` — a deadline to race a promise against, or a backoff between
 * retries and result polls.
 */
export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** The rejection every abort-aware wait settles with; `isAbortError` recognises it. */
export const createAbortError = (): DOMException =>
  new DOMException('Aborted', 'AbortError');

/** True for the rejection an aborted wait produces (`whenVisible`, `abortable`, …). */
export const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError';

/**
 * Throws an `AbortError` once `signal` has aborted — a checkpoint between the steps of a
 * long job. Hand-rolled because the jsdom version Jest runs has no
 * `AbortSignal.prototype.throwIfAborted`.
 */
export const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw createAbortError();
  }
};

/**
 * Settles with `promise`, or rejects with an `AbortError` as soon as `signal` aborts —
 * for a wait that cannot take a signal of its own.
 *
 * The work behind `promise` is not stopped, only no longer waited for; its own outcome is
 * still observed, so a late rejection never surfaces as an unhandled one.
 */
export const abortable = <T>(
  promise: Promise<T>,
  signal: AbortSignal
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const handleAbort = () => reject(createAbortError());

    // Removed by hand rather than through `addEventListener(..., { signal })`: the jsdom
    // version Jest runs does not implement the signal option.
    const cleanup = () => signal.removeEventListener('abort', handleAbort);

    promise.then(
      (value) => {
        cleanup();
        resolve(value);
      },
      (error) => {
        cleanup();
        reject(error);
      }
    );

    if (signal.aborted) {
      handleAbort();
      return;
    }

    signal.addEventListener('abort', handleAbort);
  });

/**
 * Rejects if `promise` has not settled within `timeoutMs`.
 *
 * A request with no timeout of its own (react-query mutations, a `<img>`/canvas pipeline)
 * that never settles leaves every `await` after it — and the `finally` that resets UI
 * state — unreachable, so the screen sits on a spinner with no way out.
 */
export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
